import { Link } from "react-router-dom";
import { ArrowUpRight, Heart } from "@phosphor-icons/react";
import { FadeUp, SectionLabel } from "@/components/Reveal";
import { FOUNDER, GROUP, LOGO } from "@/data/content";

export default function About() {
  return (
    <div data-testid="page-about" className="pb-10">
      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 md:pt-28">
        <SectionLabel className="mb-8">About · The Founder</SectionLabel>
        <FadeUp>
          <h1 className="font-serif text-[12vw] md:text-[8vw] leading-[0.92] tracking-tight max-w-5xl">
            More than yoga.<br />
            <span className="font-serif-italic text-teal">A community.</span>
          </h1>
        </FadeUp>
        <FadeUp delay={0.2} className="mt-10 max-w-2xl text-lg font-light leading-relaxed text-charcoal/75">
          Flowtastics is the bridge between counseling and creativity — where mindful movement, strength, and community meet on the mat.
        </FadeUp>
      </section>

      {/* FOUNDER EDITORIAL */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-24 md:mt-36 grid lg:grid-cols-12 gap-10 lg:gap-16" data-testid="about-founder">
        <FadeUp className="lg:col-span-5">
          <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-blush/40 sticky top-28">
            <img src={FOUNDER} alt="Sophia Vito" className="w-full h-full object-cover" />
          </div>
          <p className="text-[10px] uppercase tracking-editorial text-charcoal/50 mt-4">Sophia Vito · Founder &amp; Lead Instructor</p>
        </FadeUp>
        <FadeUp delay={0.1} className="lg:col-span-7 space-y-7 text-lg font-light leading-relaxed text-charcoal/85">
          <p className="font-serif italic text-3xl md:text-4xl text-charcoal leading-[1.15]">
            "I want my students to leave each class feeling stronger, more present, and filled with gratitude."
          </p>
          <p>Over the past year, I've deepened my work in the counseling field while continuing to grow as a yoga instructor. I've been teaching yoga for five years and recently expanded my practice to include strength training — blending movement with mindfulness in a way that supports both the body and the mind.</p>
          <p>My passion for the arts, holistic wellness, and connecting through social events has shaped both my personal and professional life. My teaching style is rooted in grounding and empowerment.</p>
          <p>Inspired by both the counseling and creative worlds, I've found purpose in connecting with women who express their own form of art through the businesses they build and the nonprofits they support. This passion led me to create <em className="text-teal not-italic font-medium">Flowtastics</em> — a wellness community that blends mindful yoga flows with the healing principles of counseling.</p>
          <p>Through Flowtastics, I partner with women-owned businesses and mission-driven organizations to host pop-up events that celebrate creativity, strength, and connection. In this space, we've truly built <span className="font-serif italic text-teal">girlhood</span> — a supportive, empowering community rooted in authenticity, trust, and shared growth.</p>
          <p className="font-serif italic text-2xl text-charcoal pt-6 border-l-2 border-gold pl-6">This is more than yoga — it's about weaving art, community, and empowerment into every flow.</p>
          <p className="font-script text-3xl text-teal mt-4">— Sophia</p>
        </FadeUp>
      </section>

      {/* PHILOSOPHY PILLARS */}
      <section className="bg-cream mt-28 md:mt-40 py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <FadeUp>
            <SectionLabel className="mb-6">Philosophy</SectionLabel>
            <h2 className="font-serif text-5xl md:text-6xl tracking-tight leading-[1.02] max-w-3xl">
              Four pillars. One <span className="font-serif-italic text-teal">flow.</span>
            </h2>
          </FadeUp>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mt-16">
            {[
              { n: "01", t: "Grounding" },
              { n: "02", t: "Empowerment" },
              { n: "03", t: "Creativity" },
              { n: "04", t: "Service" },
            ].map((p, i) => (
              <FadeUp key={p.n} delay={i * 0.08} className="border-t border-charcoal/20 pt-6">
                <div className="text-[10px] uppercase tracking-editorial text-gold">{p.n}</div>
                <h3 className="font-serif text-3xl mt-4">{p.t}</h3>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* GROUP IMAGE */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-28">
        <FadeUp className="rounded-3xl overflow-hidden aspect-[16/9]">
          <img src={GROUP} alt="The Flowtastics community" className="w-full h-full object-cover" />
        </FadeUp>
        <FadeUp delay={0.15} className="mt-10 max-w-3xl">
          <p className="font-serif italic text-3xl md:text-4xl leading-tight">"In this space, we've truly built <span className="text-teal">girlhood</span>."</p>
        </FadeUp>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-28 md:mt-40 text-center">
        <FadeUp>
          <h2 className="font-serif text-5xl md:text-6xl tracking-tight">Come flow with us.</h2>
          <a href="https://www.momoyoga.com/flowtastics/teacher-profile" target="_blank" rel="noreferrer" className="mt-10 inline-flex items-center gap-3 rounded-full bg-charcoal text-cream px-7 py-4 text-[11px] tracking-editorial uppercase hover:bg-teal transition-all" data-testid="about-cta-book">
            Book on Momoyoga <ArrowUpRight size={16} weight="light" />
          </a>
        </FadeUp>
      </section>
    </div>
  );
}
